import { DELIVERY_OPTIONS_EVOUCHER } from '@virginexperiencedays/products/src/types';
import type { Product } from '@virginexperiencedays/products';

export type Overview = {
  sku: string;
  about: string;
  isCollectionProduct?: boolean;
  features: {
    numberOfChoices?: number;
    recipientChoice?: boolean;
    numberOfPeople: Product['numberOfPeople'];
    validity?: Product['validity'];
    // number of locations
    // string if generic location (e.g. 'UK Wide - Over 100 sites')
    locations: number | string;
    isInstantDelivery: boolean;
  };
};

export const overviewExtractor = (product: Product): Overview => {
  const { description, info, isCollectionProduct, numberOfPeople, validity } = product || {};

  const texts = info?.texts;

  const about = isCollectionProduct
    ? texts?.find(
        (content) =>
          content?.name?.toLowerCase() === 'choice vouchers long description - pre-sale only'
      )?.content
    : description;

  const numberOfChoices =
    isCollectionProduct && Array.isArray(product.productChoices)
      ? product.productChoices.length
      : undefined;

  const { uncategorized, categorized } = Array.isArray(product?.locations)
    ? product.locations.reduce(
        (data, location) => {
          const key =
            !location?.region || location.region.toLowerCase() === 'uncategorized'
              ? 'uncategorized'
              : 'categorized';
          return {
            ...data,
            [key]: [...(data.categorized || []), location?.name],
          };
        },
        { uncategorized: [], categorized: [] }
      )
    : { uncategorized: [], categorized: [] };

  const locations =
    uncategorized.length && !categorized.length ? uncategorized?.[0] : categorized.length;

  const isInstantDelivery = product?.deliveryOptions?.some((option) =>
    DELIVERY_OPTIONS_EVOUCHER.includes(option?.sourceCode)
  );

  // Check if the voucher has a non-fixed validity period
  const hasValidityMonths = validity?.validityMonths > 0;
  // Check if the voucher has a fixed expiry date
  const hasFixedExpiryDate = validity?.validityMonths === 0 && !!validity?.expiryDate?.length;
  const fixedExpiryDateFormatted = formatExpiryDate(validity?.expiryDate);

  return {
    sku: product?.promocode ? `${product?.sku} ${product?.promocode}` : `${product?.sku}`,
    isCollectionProduct,
    about,
    features: {
      isInstantDelivery,
      locations,
      numberOfChoices,
      numberOfPeople,
      recipientChoice: isCollectionProduct,
      validity: {
        validityMonths: hasValidityMonths ? validity?.validityMonths : undefined,
        expiryDate:
          hasFixedExpiryDate && fixedExpiryDateFormatted?.length
            ? fixedExpiryDateFormatted
            : undefined,
      },
    },
  };
};

/**
 * Formats a given date string into a user-friendly date format.
 *
 * @param date - The date string to format
 * @returns The formatted date string in 'day month year' format or null if date is missing or invalid
 */
const formatExpiryDate = (date?: string): string | null => {
  if (!date) return null;

  try {
    return new Date(date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Invalid date format:', error);

    return null;
  }
};
